import { useEffect, useMemo, useState } from 'react';
import { PMPartsAndProcedure } from '../../types/pmTypes';
import { createServiceItem, updateServiceItem } from '../../graphql/companyResourceQueries';
import { createExpenseCategory, fetchExpenseCategoriesByCompany } from '../../graphql/costTrackingQueries';
import { findExistingCategoryName, sortVendorsByName } from '../../utils/companyResourceUtils';
import { useUserContext } from '../../contexts/UserContext';
import { Btn, BtnIconOnly, IconClose, InputField, SearchSuggest } from '../ui';
import { UnitOfMeasureSearchSuggest } from '../pages-equipment/UnitOfMeasureSearchSuggest';
import { useCompanyUnitsOfMeasure } from '../../hooks/useCompanyUnitsOfMeasure';
import {
    findSuggestedCatalogUnitOfMeasure,
    isExactCatalogUnitOfMeasure,
} from '../../utils/unitOfMeasureUtils';
import { addCompanyUnitOfMeasureFromLabel } from '../../utils/companyUnitOfMeasureCatalog';
import { findMatchingListing, findPartVendorConflict } from '../pages-forms/formBuilderServiceCatalog';
import {
    addVendorFromName,
    findVendorByIdOrName,
    findVendorByName,
    type VendorCatalogItem,
} from '../../utils/vendorCatalog';
import type { ExpenseCategory } from '../../types/costTrackingTypes';

// --- Main Modal ---
interface ServiceItemModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (saved?: { category: string }) => Promise<void>;
    editItem: PMPartsAndProcedure | null;
    isCopy?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    vendors: any[];
    categories: string[];
    allItems?: PMPartsAndProcedure[];
    companyId: string;
    /** Prefills category when adding a new item from a category accordion. */
    defaultCategory?: string;
    onVendorCreated?: (vendor: VendorCatalogItem) => void;
}

export const ServiceItemModal = ({
    isOpen, onClose, onSave, editItem, isCopy = false, vendors, categories, allItems = [], companyId, defaultCategory = '', onVendorCreated,
}: ServiceItemModalProps) => {
    const { companyUser, companyOwner, company } = useUserContext();
    const currentUserId = companyUser?.id || companyOwner?.id || '';
    const currentUserName = companyUser?.name || companyOwner?.name || '';
    const costTrackingEnabled = Boolean(company?.costTrackingModuleEnrollment);

    // --- Form state ---
    const [category, setCategory] = useState(editItem?.category || defaultCategory || '');
    const [newCategory, setNewCategory] = useState('');

    const [manufacturer, setManufacturer] = useState(editItem?.manufacturer || '');
    const [newManufacturer, setNewManufacturer] = useState('');

    const [partNumber, setPartNumber] = useState(editItem?.partNumber || '');
    const [newPartNumber, setNewPartNumber] = useState('');

    const [itemName, setItemName] = useState(editItem?.itemName || '');

    const [vendorId, setVendorId] = useState(editItem?.vendorId || '');
    const [createdVendors, setCreatedVendors] = useState<VendorCatalogItem[]>([]);

    const [price, setPrice] = useState(editItem?.price?.toString() || '');
    const [unitOfMeasure, setUnitOfMeasure] = useState(editItem?.unitOfMeasure || '');
    const [expenseCategories, setExpenseCategories] = useState<ExpenseCategory[]>([]);
    const [expenseCategoryId, setExpenseCategoryId] = useState(editItem?.expenseCategoryId || '');

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);

    const originalUnitOfMeasure = editItem?.unitOfMeasure || '';
    const { items: umCatalog, catalogOptions } = useCompanyUnitsOfMeasure(companyId);

    useEffect(() => {
        if (!isOpen || !companyId || !costTrackingEnabled) return;
        let isCancelled = false;
        void fetchExpenseCategoriesByCompany(companyId)
            .then((rows) => {
                if (isCancelled) return;
                // The generated model has a nullable startingBalance and extra fields;
                // normalize to the local ExpenseCategory shape.
                setExpenseCategories(
                    (rows || [])
                        .filter((row) => Boolean(row?.id && row.name))
                        .map((row) => ({
                            id: row.id,
                            companyId: row.companyId,
                            name: row.name,
                            startingBalance: row.startingBalance ?? 0,
                            createdAt: row.createdAt,
                            updatedAt: row.updatedAt,
                        })),
                );
            })
            .catch((err) => {
                if (!isCancelled) console.error('Failed to load cost categories:', err);
            });
        return () => {
            isCancelled = true;
        };
    }, [isOpen, companyId, costTrackingEnabled]);

    useEffect(() => {
        if (!isCopy || !originalUnitOfMeasure || catalogOptions.length === 0) return;
        if (isExactCatalogUnitOfMeasure(originalUnitOfMeasure, catalogOptions)) return;
        const suggested = findSuggestedCatalogUnitOfMeasure(originalUnitOfMeasure, catalogOptions);
        if (suggested) setUnitOfMeasure(suggested.value);
    }, [isCopy, originalUnitOfMeasure, catalogOptions]);

    const legacyUmSuggestion = useMemo(() => {
        if (!originalUnitOfMeasure || isCopy) return undefined;
        if (isExactCatalogUnitOfMeasure(unitOfMeasure, catalogOptions)) return undefined;
        return findSuggestedCatalogUnitOfMeasure(originalUnitOfMeasure, catalogOptions);
    }, [catalogOptions, isCopy, originalUnitOfMeasure, unitOfMeasure]);

    const showLegacyUmHint =
        Boolean(originalUnitOfMeasure) &&
        !isCopy &&
        unitOfMeasure === originalUnitOfMeasure &&
        catalogOptions.length > 0 &&
        !isExactCatalogUnitOfMeasure(originalUnitOfMeasure, catalogOptions);

    // --- Derived option lists ---
    const resolvedCategory = category === '__new__' ? newCategory.trim() : category;
    const resolvedManufacturer = manufacturer === '__new__' ? newManufacturer.trim() : manufacturer;

    const manufacturersForCategory = useMemo(() => {
        if (!resolvedCategory) return [];
        const mfrs = new Set<string>();
        allItems.filter(i => i.category === resolvedCategory && i.manufacturer).forEach(i => mfrs.add(i.manufacturer!));
        return [...mfrs].sort();
    }, [allItems, resolvedCategory]);

    const itemNumbersForManufacturer = useMemo(() => {
        if (!resolvedCategory || !resolvedManufacturer) return [];
        const nums = new Set<string>();
        allItems.filter(i => i.category === resolvedCategory && i.manufacturer === resolvedManufacturer && i.partNumber)
            .forEach(i => nums.add(i.partNumber!));
        return [...nums].sort();
    }, [allItems, resolvedCategory, resolvedManufacturer]);

    const activeVendors = useMemo(() => {
        const merged = new Map<string, VendorCatalogItem>();
        for (const vendor of vendors as VendorCatalogItem[]) {
            if (!vendor?.id || vendor.archived) continue;
            merged.set(vendor.id, vendor);
        }
        for (const vendor of createdVendors) {
            if (!vendor?.id || vendor.archived) continue;
            merged.set(vendor.id, vendor);
        }
        return sortVendorsByName([...merged.values()]);
    }, [createdVendors, vendors]);

    if (!isOpen) return null;

    const validate = () => {
        const errs: Record<string, string> = {};
        if (!itemName.trim()) errs.itemName = 'Description is required';
        const finalCat = category === '__new__' ? newCategory.trim() : category;
        if (!finalCat) errs.category = 'Category is required';
        const finalManufacturer = manufacturer === '__new__' ? newManufacturer.trim() : manufacturer;
        if (!finalManufacturer) errs.manufacturer = 'Manufacturer is required';
        const finalPartNumber = partNumber === '__new__' ? newPartNumber.trim() : partNumber;
        const resolvedVendor = findVendorByIdOrName(activeVendors, vendorId);
        if (!vendorId.trim()) errs.vendor = 'Vendor is required';
        const vendorName = resolvedVendor?.name || '';
        const listingKey = {
            itemName: itemName.trim(),
            manufacturer: finalManufacturer,
            partNumber: finalPartNumber,
            vendorId: resolvedVendor?.id || vendorId || null,
            vendorName: vendorName || null,
        };
        const excludeId = editItem && !isCopy ? editItem.id : undefined;
        const partConflict = findPartVendorConflict(allItems, listingKey, excludeId);
        const conflict = partConflict || findMatchingListing(allItems, finalCat, listingKey, excludeId);
        if (partConflict) {
            errs._form =
                'This part number is already listed for this manufacturer and vendor. A different vendor can use the same part number.';
        } else if (conflict) {
            errs._form =
                'A listing with this name, vendor, manufacturer, and part number already exists. Change vendor, manufacturer, or part number to add another.';
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            let finalVendorId = vendorId.trim() || null;
            let vendorName: string | null = null;
            if (finalVendorId) {
                const existingVendor = findVendorByIdOrName(activeVendors, finalVendorId);
                if (existingVendor) {
                    finalVendorId = existingVendor.id;
                    vendorName = existingVendor.name?.trim() || null;
                } else {
                    const created = await addVendorFromName(companyId, finalVendorId, activeVendors);
                    finalVendorId = created.id;
                    vendorName = created.name?.trim() || finalVendorId;
                    setCreatedVendors((prev) =>
                        prev.some((vendor) => vendor.id === created.id) ? prev : [...prev, created],
                    );
                    onVendorCreated?.(created);
                }
            }

            let finalCategory = category === '__new__' ? newCategory.trim() : category;
            let finalManufacturer = manufacturer === '__new__' ? newManufacturer.trim() : manufacturer;
            const finalPartNumber = partNumber === '__new__' ? newPartNumber.trim() : partNumber;

            // Case-insensitive normalization: use existing casing if a match exists
            if (finalCategory) {
                const existingCat = findExistingCategoryName(categories, finalCategory);
                if (existingCat) finalCategory = existingCat;
            }
            if (finalManufacturer) {
                const allMfrs = new Set<string>();
                allItems.filter(i => i.manufacturer).forEach(i => allMfrs.add(i.manufacturer!));
                const existingMfr = [...allMfrs].find(m => m.toLowerCase() === finalManufacturer!.toLowerCase());
                if (existingMfr) finalManufacturer = existingMfr;
            }
            let finalUnitOfMeasure = unitOfMeasure.trim() || null;
            const isNewRow = !editItem || isCopy;
            if (finalUnitOfMeasure) {
                if (!isNewRow && finalUnitOfMeasure === originalUnitOfMeasure) {
                    finalUnitOfMeasure = originalUnitOfMeasure;
                } else if (isExactCatalogUnitOfMeasure(finalUnitOfMeasure, catalogOptions)) {
                    const exact = catalogOptions.find(
                        (item) =>
                            item.value === finalUnitOfMeasure ||
                            item.value.toLowerCase() === finalUnitOfMeasure!.toLowerCase() ||
                            item.label.toLowerCase() === finalUnitOfMeasure!.toLowerCase(),
                    );
                    if (exact) finalUnitOfMeasure = exact.value;
                } else {
                    const created = await addCompanyUnitOfMeasureFromLabel(
                        companyId,
                        finalUnitOfMeasure,
                        umCatalog,
                    );
                    finalUnitOfMeasure = created.value;
                }
            }

            const input: any = {
                companyId,
                itemName: itemName.trim(),
                category: finalCategory || null,
                itemType: 'Consumable',
                partNumber: finalPartNumber || null,
                manufacturer: finalManufacturer || null,
                vendorId: finalVendorId,
                vendorName: vendorName || null,
                unitOfMeasure: finalUnitOfMeasure,
                price: price ? parseFloat(price) : null,
            };

            if (costTrackingEnabled) {
                const typed = expenseCategoryId.trim();
                const selected = expenseCategories.find(
                    (row) =>
                        row.id === typed ||
                        row.name.trim().toLowerCase() === typed.toLowerCase(),
                );
                if (!typed) {
                    input.expenseCategoryId = null;
                    input.expenseCategoryName = null;
                } else if (selected) {
                    input.expenseCategoryId = selected.id;
                    input.expenseCategoryName = selected.name;
                } else {
                    const created = await createExpenseCategory({
                        companyId,
                        name: typed,
                        startingBalance: 0,
                    });
                    if (!created?.id) throw new Error('Failed to add cost category');
                    input.expenseCategoryId = created.id;
                    input.expenseCategoryName = created.name || typed;
                }
            }

            if (editItem && !isCopy) {
                await updateServiceItem({ id: editItem.id, ...input });
            } else {
                await createServiceItem({ ...input, createdByUserId: currentUserId, createdByUserName: currentUserName });
            }
            await onSave({ category: finalCategory || 'Uncategorized' });
        } catch (err) {
            console.error('Error saving:', err);
            setErrors({ _form: 'Failed to save. Please try again.' });
        } finally { setSaving(false); }
    };

    // --- Option arrays for combo-boxes ---
    const categoryOptions = categories.map(c => ({ value: c, label: c }));
    const manufacturerOptions = manufacturersForCategory.map(m => ({ value: m, label: m }));
    const partNumberOptions = itemNumbersForManufacturer.map(n => ({ value: n, label: n }));
    const vendorOptions = activeVendors.map((vendor) => ({
        value: vendor.id,
        label: vendor.name || vendor.id,
    }));
    const rememberVendor = (vendor: VendorCatalogItem) => {
        setCreatedVendors((prev) =>
            prev.some((item) => item.id === vendor.id) ? prev : [...prev, vendor],
        );
        onVendorCreated?.(vendor);
        setVendorId(vendor.id);
        setErrors((prev) => {
            if (!prev.vendor) return prev;
            const { vendor: _vendor, ...rest } = prev;
            return rest;
        });
    };

    const handleAddVendor = (text: string) => {
        const trimmed = text.trim();
        if (!trimmed) return;
        const existing = findVendorByName(activeVendors, trimmed);
        if (existing) {
            setVendorId(existing.id);
            return;
        }
        void addVendorFromName(companyId, trimmed, activeVendors)
            .then(rememberVendor)
            .catch((err) => {
                console.error('Failed to add vendor:', err);
                setErrors((prev) => ({ ...prev, vendor: 'Failed to add vendor. Please try again.' }));
            });
    };

    const categoryValue = category === '__new__' ? newCategory : category;
    const manufacturerValue = manufacturer === '__new__' ? newManufacturer : manufacturer;
    const partNumberValue = partNumber === '__new__' ? newPartNumber : partNumber;

    const handleCategoryChange = (val: string) => {
        const exactMatch = categories.find((c) => c.toLowerCase() === val.toLowerCase());
        if (exactMatch) {
            setCategory(exactMatch);
            setNewCategory('');
        } else if (val.trim()) {
            setCategory('__new__');
            setNewCategory(val.trim());
        } else {
            setCategory('');
            setNewCategory('');
        }

        if (!editItem) {
            setManufacturer('');
            setNewManufacturer('');
            setPartNumber('');
            setNewPartNumber('');
        }
    };

    const handleManufacturerChange = (val: string) => {
        const exactMatch = manufacturersForCategory.find((m) => m.toLowerCase() === val.toLowerCase());
        if (exactMatch) {
            setManufacturer(exactMatch);
            setNewManufacturer('');
        } else if (val.trim()) {
            setManufacturer('__new__');
            setNewManufacturer(val.trim());
        } else {
            setManufacturer('');
            setNewManufacturer('');
        }

        if (!editItem) {
            setPartNumber('');
            setNewPartNumber('');
        }
    };

    const handlePartNumberChange = (val: string) => {
        const exactMatch = itemNumbersForManufacturer.find((n) => n.toLowerCase() === val.toLowerCase());
        if (exactMatch) {
            setPartNumber(exactMatch);
            setNewPartNumber('');
        } else if (val.trim()) {
            setPartNumber('__new__');
            setNewPartNumber(val.trim());
        } else {
            setPartNumber('');
            setNewPartNumber('');
        }
    };

    const primaryLabel = saving
        ? 'Saving...'
        : editItem && !isCopy
            ? 'Update Item'
            : 'Save Item';

    const itemNameField = (
        <div className="cr-field">
            <label className="cr-field-label" htmlFor="service-item-name">
                Description <span className="cr-required" aria-hidden="true">*</span>
            </label>
            <InputField
                inputId="service-item-name"
                ariaLabel="Description"
                name="service-item-name-no-autofill"
                autoComplete="off"
                value={itemName}
                onChange={setItemName}
                placeholder="e.g. Oil Filter"
                hasError={!!errors.itemName}
                ariaDescribedBy={errors.itemName ? 'service-item-name-error' : undefined}
            />
            {errors.itemName && (
                <div id="service-item-name-error" className="cr-field-error-text" role="alert">
                    {errors.itemName}
                </div>
            )}
        </div>
    );

    const categoryField = (
        <div className="cr-field">
            <label className="cr-field-label" htmlFor="service-item-category">
                Category <span className="cr-required" aria-hidden="true">*</span>
            </label>
            <SearchSuggest
                options={categoryOptions}
                value={categoryValue}
                onChange={handleCategoryChange}
                placeholder="Type to search or add"
                newLabel="+ New Category"
                allowNew
                hasError={!!errors.category}
                ariaDescribedBy={errors.category ? 'service-item-category-error' : undefined}
                inputId="service-item-category"
            />
            {category === '__new__' && newCategory && (
                <div className="cr-field-helper">
                    New category: &ldquo;{newCategory}&rdquo;
                </div>
            )}
            {errors.category && (
                <div id="service-item-category-error" className="cr-field-error-text" role="alert">
                    {errors.category}
                </div>
            )}
        </div>
    );

    return (
        <div className="cr-modal-overlay">
            <div
                className="cr-modal cr-modal--wide"
                role="dialog"
                aria-modal="true"
                aria-labelledby="service-item-modal-title"
            >
                <div className="cr-modal-header">
                    <h2 id="service-item-modal-title" className="cr-modal-title">
                        {isCopy
                            ? `Copy ${editItem?.itemName?.trim() || 'service item'}`
                            : editItem
                                ? 'Edit Service Item'
                                : 'Add Service Item'}
                    </h2>
                    <BtnIconOnly size="sm" icon={<IconClose />} ariaLabel="Close" onClick={onClose} />
                </div>
                <form
                    className="cr-modal-form"
                    autoComplete="off"
                    onSubmit={(event) => {
                        event.preventDefault();
                        void handleSubmit();
                    }}
                >
                    <div className="cr-modal-body">
                        {errors._form && (
                            <div className="cr-error-banner" role="alert">
                                {errors._form}
                            </div>
                        )}

                        {categoryField}
                        {itemNameField}

                        <div className="cr-field">
                            <label className="cr-field-label" htmlFor="service-item-manufacturer">
                                Manufacturer <span className="cr-required" aria-hidden="true">*</span>
                            </label>
                            <SearchSuggest
                                options={manufacturerOptions}
                                value={manufacturerValue}
                                onChange={handleManufacturerChange}
                                placeholder="e.g. CAT"
                                newLabel="+ New Manufacturer"
                                allowNew
                                disabled={!resolvedCategory}
                                disabledMessage="Select a category first"
                                hasError={!!errors.manufacturer}
                                ariaDescribedBy={
                                    errors.manufacturer ? 'service-item-manufacturer-error' : undefined
                                }
                                inputId="service-item-manufacturer"
                            />
                            {manufacturer === '__new__' && newManufacturer && (
                                <div className="cr-field-helper">
                                    New manufacturer: &ldquo;{newManufacturer}&rdquo;
                                </div>
                            )}
                            {errors.manufacturer && (
                                <div id="service-item-manufacturer-error" className="cr-field-error-text" role="alert">
                                    {errors.manufacturer}
                                </div>
                            )}
                        </div>

                        <div className="cr-field">
                            <label className="cr-field-label" htmlFor="service-item-part-number">
                                Part / Item #
                            </label>
                            <SearchSuggest
                                options={partNumberOptions}
                                value={partNumberValue}
                                onChange={handlePartNumberChange}
                                placeholder="e.g. OF-123"
                                newLabel="+ New Item #"
                                allowNew
                                disabled={!resolvedCategory || !resolvedManufacturer}
                                disabledMessage={
                                    resolvedCategory ? 'Select a manufacturer first' : 'Select a category first'
                                }
                                hasError={!!errors.partNumber}
                                ariaDescribedBy={
                                    errors.partNumber
                                        ? 'service-item-part-number-error'
                                        : isCopy
                                            ? 'service-item-part-number-hint'
                                            : undefined
                                }
                                inputId="service-item-part-number"
                            />
                            {isCopy && !errors.partNumber && (
                                <div id="service-item-part-number-hint" className="cr-field-helper">
                                    Same item # is OK when vendor or manufacturer is different.
                                </div>
                            )}
                            {errors.partNumber && (
                                <div id="service-item-part-number-error" className="cr-field-error-text" role="alert">
                                    {errors.partNumber}
                                </div>
                            )}
                            {partNumber === '__new__' && newPartNumber && (
                                <div className="cr-field-helper">
                                    New item #: &ldquo;{newPartNumber}&rdquo;
                                </div>
                            )}
                        </div>

                        <div className="cr-field-row">
                            {/* Vendor */}
                            <div className="cr-field">
                                <label className="cr-field-label" htmlFor="service-item-vendor">
                                    Vendor <span className="cr-required" aria-hidden="true">*</span>
                                </label>
                                <SearchSuggest
                                    options={vendorOptions}
                                    value={vendorId}
                                    onChange={setVendorId}
                                    placeholder="Type to search or add"
                                    allowNew
                                    newLabel="+ Add vendor"
                                    resolveValue={(text) => findVendorByName(activeVendors, text)?.id || text.trim()}
                                    formatDisplayValue={(value) =>
                                        findVendorByIdOrName(activeVendors, value)?.name || value
                                    }
                                    onCommitNew={handleAddVendor}
                                    hasError={!!errors.vendor}
                                    ariaDescribedBy={
                                        errors.vendor ? 'service-item-vendor-error' : 'service-item-vendor-hint'
                                    }
                                    inputId="service-item-vendor"
                                />
                                {errors.vendor ? (
                                    <div id="service-item-vendor-error" className="cr-field-error-text" role="alert">
                                        {errors.vendor}
                                    </div>
                                ) : (
                                    <div id="service-item-vendor-hint" className="cr-field-helper">
                                        Not in the list? Type the name and add it.
                                    </div>
                                )}
                            </div>

                            {/* Unit of Measure */}
                            <div className="cr-field">
                                <label className="cr-field-label" htmlFor="service-item-unit">
                                    Unit of Measure
                                </label>
                                <UnitOfMeasureSearchSuggest
                                    value={unitOfMeasure}
                                    onChange={setUnitOfMeasure}
                                    includeEquipmentUnits={false}
                                    extraValues={
                                        !isCopy && originalUnitOfMeasure ? [originalUnitOfMeasure] : []
                                    }
                                    placeholder="Search or add"
                                    inputId="service-item-unit"
                                />
                                {showLegacyUmHint && (
                                    <div className="cr-field-helper cr-field-helper-warning">
                                        {legacyUmSuggestion ? (
                                            <>
                                                This item still uses “{originalUnitOfMeasure}”. The
                                                company catalog has “{legacyUmSuggestion.label}”.
                                                Existing records stay as they are until you update
                                                this field.{' '}
                                                <Btn
                                                    variant="link"
                                                    buttonText={`Use ${legacyUmSuggestion.label}`}
                                                    buttonOnClick={() =>
                                                        setUnitOfMeasure(legacyUmSuggestion.value)
                                                    }
                                                />
                                            </>
                                        ) : (
                                            <>
                                                This item still uses “{originalUnitOfMeasure}”, which
                                                is not in the company catalog. Choose a catalog unit
                                                to update it, or add this one with + Add custom UM.
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Estimated Cost */}
                        <div className="cr-field">
                            <label className="cr-field-label" htmlFor="service-item-price">
                                Estimated Cost
                            </label>
                            <InputField
                                inputId="service-item-price"
                                ariaLabel="Estimated Cost"
                                type="number"
                                step="0.01"
                                min={0}
                                name="service-item-price-no-autofill"
                                autoComplete="off"
                                value={price}
                                onChange={setPrice}
                                placeholder="$0.00"
                            />
                            {editItem && !isCopy && (
                                <div className="cr-field-helper">
                                    This is the current catalog price. Cost tracking entries already recorded keep the price they were saved with.
                                </div>
                            )}
                        </div>

                        {costTrackingEnabled && (
                            <div className="cr-field">
                                <label className="cr-field-label" htmlFor="service-item-cost-category">
                                    Cost Category
                                </label>
                                <SearchSuggest
                                    options={[...expenseCategories]
                                        .sort((a, b) =>
                                            (a.name || '').localeCompare(b.name || '', undefined, {
                                                sensitivity: 'base',
                                            }),
                                        )
                                        .map((row) => ({
                                            value: row.id,
                                            label: row.name,
                                        }))}
                                    value={expenseCategoryId}
                                    onChange={setExpenseCategoryId}
                                    placeholder="Type to search or add"
                                    allowNew
                                    newLabel="+ Add cost category"
                                    resolveValue={(text) =>
                                        expenseCategories.find(
                                            (row) => row.name.trim().toLowerCase() === text.trim().toLowerCase(),
                                        )?.id || text.trim()
                                    }
                                    formatDisplayValue={(value) =>
                                        expenseCategories.find((row) => row.id === value)?.name || value
                                    }
                                    clearable
                                    hasError={!!errors.expenseCategory}
                                    ariaDescribedBy={
                                        errors.expenseCategory
                                            ? 'service-item-cost-category-error'
                                            : 'service-item-cost-category-hint'
                                    }
                                    onCommitNew={(text) => {
                                        const trimmed = text.trim();
                                        if (!trimmed) return;
                                        const existing = expenseCategories.find(
                                            (row) => row.name.trim().toLowerCase() === trimmed.toLowerCase(),
                                        );
                                        if (existing) {
                                            setExpenseCategoryId(existing.id);
                                            return;
                                        }
                                        void createExpenseCategory({
                                            companyId,
                                            name: trimmed,
                                            startingBalance: 0,
                                        })
                                            .then((created) => {
                                                if (!created?.id) throw new Error('Failed to add cost category');
                                                const row = {
                                                    id: created.id,
                                                    companyId,
                                                    name: created.name || trimmed,
                                                    startingBalance: created.startingBalance ?? 0,
                                                    createdAt: created.createdAt || '',
                                                    updatedAt: created.updatedAt || '',
                                                };
                                                setExpenseCategories((prev) =>
                                                    prev.some((item) => item.id === row.id) ? prev : [...prev, row],
                                                );
                                                setExpenseCategoryId(row.id);
                                            })
                                            .catch((err) => {
                                                console.error('Failed to add cost category:', err);
                                                setErrors((prev) => ({
                                                    ...prev,
                                                    expenseCategory: 'Failed to add cost category. Please try again.',
                                                }));
                                            });
                                    }}
                                    inputId="service-item-cost-category"
                                />
                                <div id="service-item-cost-category-hint" className="cr-field-helper">
                                    When this item is used, including on a procedure, the dollars total here. Leave it blank to keep Daily Service or PM Services.
                                </div>
                                {errors.expenseCategory && (
                                    <div id="service-item-cost-category-error" className="cr-field-error-text" role="alert">
                                        {errors.expenseCategory}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="cr-modal-footer">
                        <Btn variant="secondary-outlined" buttonText="Cancel" buttonOnClick={onClose} />
                        <Btn
                            variant="primary"
                            buttonType="submit"
                            buttonText={primaryLabel}
                            buttonDisabled={saving}
                        />
                    </div>
                </form>
            </div>
        </div>
    );
};
